/**
 * The one upload policy for a questionnaire asset's media (B23): the backend
 * route and the builder's pickers and checks both read it, so a file one side
 * accepts is never refused by the other for a reason the user cannot see.
 */

/** The media types a questionnaire asset takes. */
export const QUESTIONNAIRE_ASSET_UPLOAD_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "application/pdf",
  // Video assets: analyzed server-side into a keyframe storyboard and brief.
  "video/mp4",
  "video/webm",
  "video/quicktime",
] as const;

export type QuestionnaireAssetUploadMimeType =
  (typeof QUESTIONNAIRE_ASSET_UPLOAD_MIME_TYPES)[number];

export const QUESTIONNAIRE_ASSET_VIDEO_MIME_TYPES = [
  "video/mp4",
  "video/webm",
  "video/quicktime",
] as const satisfies ReadonlyArray<QuestionnaireAssetUploadMimeType>;

/**
 * The raster image types a chat message carries inline (rooms and 1:1): the
 * asset image types above minus SVG, which the chat composer rasterizes
 * before it sends (studies rasterize it at upload). Same list, so a format a
 * study takes is never refused by a chat send.
 */
export const CONVERSATION_IMAGE_MIME_TYPES = QUESTIONNAIRE_ASSET_UPLOAD_MIME_TYPES.filter(
  (mimeType) => mimeType.startsWith("image/") && mimeType !== "image/svg+xml",
);

/**
 * Whether a chat image's declared type is in {@link CONVERSATION_IMAGE_MIME_TYPES}.
 * `image/jpg` is the common non-standard spelling of `image/jpeg` and is read as it.
 */
export function isConversationImageMime(mimeType: string | null | undefined): boolean {
  const normalized = normalizeUploadMime(mimeType);
  const canonical = normalized === "image/jpg" ? "image/jpeg" : normalized;
  return (CONVERSATION_IMAGE_MIME_TYPES as ReadonlyArray<string>).includes(canonical);
}

/** Per-kind byte caps: images and PDFs 10 MB, video 500 MB. */
export const QUESTIONNAIRE_ASSET_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const QUESTIONNAIRE_ASSET_VIDEO_MAX_BYTES = 500 * 1024 * 1024;

/**
 * Lowercase and strip MIME parameters: `video/webm;codecs=vp9` is
 * `video/webm`. Browsers attach codec parameters to recorded blobs, so
 * membership is checked on the bare type only.
 */
export function normalizeUploadMime(mimeType: string | null | undefined): string {
  return (mimeType ?? "").split(";")[0].trim().toLowerCase();
}

export function isQuestionnaireAssetUploadMime(
  mimeType: string | null | undefined,
): boolean {
  return (QUESTIONNAIRE_ASSET_UPLOAD_MIME_TYPES as ReadonlyArray<string>).includes(
    normalizeUploadMime(mimeType),
  );
}

export function isQuestionnaireAssetVideoMime(
  mimeType: string | null | undefined,
): boolean {
  return (QUESTIONNAIRE_ASSET_VIDEO_MIME_TYPES as ReadonlyArray<string>).includes(
    normalizeUploadMime(mimeType),
  );
}

/** The byte cap for a file of `mimeType`'s kind. */
export function questionnaireAssetUploadMaxBytes(
  mimeType: string | null | undefined,
): number {
  return isQuestionnaireAssetVideoMime(mimeType)
    ? QUESTIONNAIRE_ASSET_VIDEO_MAX_BYTES
    : QUESTIONNAIRE_ASSET_IMAGE_MAX_BYTES;
}

/** Why a file cannot be uploaded as an asset's media. */
export type QuestionnaireAssetUploadRefusal = "type_unsupported" | "too_large";

/**
 * The refusal the upload route would answer for `file`, from its declared
 * type and size, or null when the route would take it. The route still
 * checks the bytes; this is the builder's check before it sends them.
 */
export function questionnaireAssetUploadRefusal(file: {
  type: string;
  size: number;
}): QuestionnaireAssetUploadRefusal | null {
  if (!isQuestionnaireAssetUploadMime(file.type)) return "type_unsupported";
  return file.size > questionnaireAssetUploadMaxBytes(file.type)
    ? "too_large"
    : null;
}
